export const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

export const addMonthsClamped = (date: Date, amount: number) => {
  const result = new Date(date);
  result.setDate(1);
  result.setMonth(result.getMonth() + amount);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(date.getDate(), lastDay));
  return result;
};

export const buildMonthDays = (month: Date): Array<Date | null> => {
  const firstDay = startOfMonth(month);
  const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
  const days: Array<Date | null> = Array.from({ length: firstDay.getDay() }, () => null);

  for (let day = 1; day <= lastDay.getDate(); day += 1) {
    days.push(new Date(month.getFullYear(), month.getMonth(), day));
  }

  while (days.length % 7 !== 0) days.push(null);

  return days;
};

export const isSameDay = (left: Date | null | undefined, right: Date | null | undefined) =>
  Boolean(
    left &&
    right &&
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate(),
  );

export const getDayTimestamp = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const isDateDisabled = (
  date: Date,
  minDate: Date | null | undefined,
  maxDate: Date | null | undefined,
) =>
  Boolean(
    (minDate && getDayTimestamp(date) < getDayTimestamp(minDate)) ||
    (maxDate && getDayTimestamp(date) > getDayTimestamp(maxDate)),
  );

export const clampDateToRange = (
  date: Date,
  minDate: Date | null | undefined,
  maxDate: Date | null | undefined,
) => {
  if (minDate && getDayTimestamp(date) < getDayTimestamp(minDate)) return new Date(minDate);
  if (maxDate && getDayTimestamp(date) > getDayTimestamp(maxDate)) return new Date(maxDate);
  return date;
};

export const isDateInRange = (
  date: Date,
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
) => {
  if (!startDate || !endDate) return false;
  const timestamp = getDayTimestamp(date);
  return timestamp > getDayTimestamp(startDate) && timestamp < getDayTimestamp(endDate);
};

export const copyTime = (date: Date, source?: Date | null) => {
  const result = new Date(date);
  if (source) {
    result.setHours(
      source.getHours(),
      source.getMinutes(),
      source.getSeconds(),
      source.getMilliseconds(),
    );
  }
  return result;
};

export const toTimeValue = (date?: Date | null) => {
  if (!date) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const setTimeValue = (date: Date, value: string) => {
  const [hours, minutes] = value.split(':').map(Number);
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  const result = new Date(date);
  result.setHours(hours, minutes, 0, 0);
  return result;
};

export const toDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`;
